import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildVirtualUserPayload } from '../utils/easyModePayload';
import { toaster } from '../utils/toast';
import useDemandCreditCheck from './useDemandCreditCheck';
import usePfqAxios from './usePfqAxios';

const VIRTUAL_USER_ENDPOINT = '/virtual-user';

// Re-runs the Easy Mode assistant from the overview after a human has resolved gaps in Case Intake.
// The gap report is only ever written by a run, so re-running is the only way to clear a stale one; the
// PFQ guards against a second run while one is active and against re-spending a credit on a done demand.
const useVirtualUserRerun = ({ caseId, caseInfo }) => {
  const [isRerunning, setIsRerunning] = useState(false);
  const navigate = useNavigate();
  const { postData } = usePfqAxios();
  const { confirmDemandCredit } = useDemandCreditCheck();

  const rerun = async () => {
    if (isRerunning || !caseInfo) return;

    setIsRerunning(true);

    try {
      if (!(await confirmDemandCredit({ plaintiffCount: caseInfo.plaintiffCount }))) return;

      // the payload is rebuilt from the saved case, so it always matches what the PFQ validates against
      const response = await postData(VIRTUAL_USER_ENDPOINT, buildVirtualUserPayload({
        caseId,
        intakeValues: {
          caseInfo: {
            caseName: caseInfo.caseName ?? '',
            caseType: caseInfo.caseType,
            stateOfIncident: caseInfo.stateOfIncident,
            dateOfIncident: caseInfo.dateOfIncident
          },
          plaintiffCount: caseInfo.plaintiffCount,
          defendantCount: caseInfo.defendantCount
        },
        additionalContext: caseInfo.additionalContext ?? ''
      }));

      if (response?.success === false) {
        throw new Error(response.message);
      }

      toaster({ message: 'The assistant is reviewing the case again', success: true });
      navigate('/cases');
    } catch (error) {
      toaster({ message: error?.message ?? 'Could not re-run the assistant', success: false });
    } finally {
      setIsRerunning(false);
    }
  };

  return { rerun, isRerunning };
};

export default useVirtualUserRerun;
